import type { ProjectDefinition } from "../../../types";
import { type CellValue, type FuncionProjectSpec, excelDate, seededRandom } from "../spec";

const SHEET = "MD Clientes";
const TABLE = "VentasMD";

// Plain first letters keep Excel's and JavaScript's alphabetical order identical.
const CLIENTES = [
  "Grupo Altamira", "Comercial Norte", "Distribuidora Luna", "Hotel Mirador", "Farmacias Vida",
  "Constructora Roble", "Café La Esquina", "Papelería Central", "Transportes Del Valle", "Clínica San José",
  "Mueblería Bravo", "Panadería Rosal",
] as const;
const CIUDADES = ["Monterrey", "Guadalajara", "Puebla", "Mérida", "León"] as const;
const VENDEDORES = ["Ana López", "Luis Martínez", "Marta Sánchez"] as const;
const PRODUCTOS = ["Licencia anual", "Soporte premium", "Capacitación", "Implementación"] as const;

const rnd = seededRandom(5150);
// Each client lives in one city; a few clients buy only once.
const CIUDAD_DE = Object.fromEntries(CLIENTES.map((c, i) => [c, CIUDADES[i % CIUDADES.length]]));
const UNA_VEZ = new Set(["Transportes Del Valle", "Mueblería Bravo", "Panadería Rosal"]);
const FRECUENTES = CLIENTES.filter((c) => !UNA_VEZ.has(c));

const compras: string[] = [...UNA_VEZ, ...Array.from({ length: 44 }, () => rnd.pick(FRECUENTES))];
// Shuffle so the one-time buyers are spread through the list.
for (let i = compras.length - 1; i > 0; i--) {
  const j = rnd.int(0, i);
  [compras[i], compras[j]] = [compras[j], compras[i]];
}

// Fecha, Cliente, Ciudad, Vendedor, Producto, Importe
const ROWS = compras
  .map((cliente) => [
    excelDate("2026-01-05") + rnd.int(0, 150),
    cliente,
    CIUDAD_DE[cliente],
    rnd.pick(VENDEDORES),
    rnd.pick(PRODUCTOS),
    rnd.round(3000, 60000, 100),
  ] as [number, string, string, string, string, number])
  .sort((a, b) => a[0] - b[0]);

type Row = (typeof ROWS)[number];
const IN = { ciudad: "Monterrey" };

const unique = <T,>(values: T[]) => [...new Set(values)];
const clientes = unique(ROWS.map((r) => r[1]));
const byText = (a: string, b: string) => a.localeCompare(b, "es", { sensitivity: "base" });

const pares = unique(ROWS.map((r) => `${r[2]}|${r[3]}`)).map((p) => p.split("|"));
const unaVez = clientes.filter((c) => ROWS.filter((r) => r[1] === c).length === 1);
const totalDe = (c: string) => ROWS.filter((r: Row) => r[1] === c).reduce((s, r) => s + r[5], 0);

const col = (values: CellValue[]) => values.map((v) => [v]);
const EXPECTED: CellValue[][][] = [
  col(clientes),
  pares,
  col(unaVez),
  col(unique(ROWS.map((r) => r[2])).sort(byText)),
  [[clientes.length]],
  col(unique(ROWS.filter((r) => r[2] === IN.ciudad).map((r) => r[1]))),
  col(clientes.map(totalDe)),
];

// Task 7 sits next to task 1 (it reads its spill with #); the rest stack below.
const ANCHORS: string[] = ["J12", "", "", "", "", "", "K12"];
let next = 12 + clientes.length + 4;
for (const i of [1, 2, 3, 4, 5]) {
  ANCHORS[i] = `J${next}`;
  next += EXPECTED[i].length + 4;
}

const LABELS = [
  "Tarea 1 · Clientes (sin repetir)",
  "Tarea 2 · Ciudad y vendedor",
  "Tarea 3 · Clientes de una sola compra",
  "Tarea 4 · Ciudades A–Z",
  "Tarea 5 · Número de clientes",
  "Tarea 6 · Clientes de la ciudad",
  "Tarea 7 · Total por cliente",
];
const FNS = [
  ["UNIQUE"], ["UNIQUE"], ["UNIQUE"], ["UNIQUE", "SORT"], ["UNIQUE", "COUNTA|ROWS"],
  ["UNIQUE", "FILTER"], ["SUMIFS"],
];

export const spec: FuncionProjectSpec = {
  projectId: "funciones-unicos",
  sheets: [
    {
      name: SHEET,
      title: "Cartera de clientes",
      subtitle: "Ventas de servicios del semestre — los resultados se derraman desde las celdas amarillas",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: ["Fecha", "Cliente", "Ciudad", "Vendedor", "Producto", "Importe"],
          rows: ROWS,
          formats: { Fecha: "dd/mm/yyyy", Importe: "$#,##0" },
        },
      ],
      inputs: [{ address: "J4", label: "Ciudad", value: IN.ciudad }],
      answers: ANCHORS.map((address, i) => ({
        address,
        label: LABELS[i],
        labelPosition: "above" as const,
        format: i === 6 ? "$#,##0" : undefined,
      })),
    },
  ],
  checks: Object.fromEntries(
    EXPECTED.map((expected, i) => [
      i + 1,
      { kind: "spill" as const, sheet: SHEET, address: ANCHORS[i], fns: FNS[i], expected },
    ])
  ),
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-unicos",
  title: "UNICOS: Cartera de clientes",
  subtitle: "Listas sin duplicados que se actualizan solas",
  description:
    "Tienes 47 ventas del semestre y el director pregunta: ¿cuántos clientes distintos tenemos?, ¿quiénes compraron una sola vez?, ¿cuánto nos compró cada uno?\n\nUNICOS quita los repetidos de una lista o de varias columnas. Combinada con ORDENAR, FILTRAR y el operador # se vuelve la base de cualquier reporte automático.",
  topics: [
    "Valores únicos de una columna",
    "Combinaciones únicas de varias columnas",
    "Valores que aparecen una sola vez",
    "Contar y ordenar resultados únicos",
    "El operador # para leer un rango derramado",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Lista de clientes",
      description: `En la hoja <strong>MD Clientes</strong>, celda <strong>${ANCHORS[0]}</strong>: muestra la lista de <strong>Clientes</strong> sin repetir.`,
      solution: [
        `Selecciona ${F(ANCHORS[0])}.`,
        `Escribe ${F("=UNICOS(VentasMD[Cliente])")}.`,
        "Los clientes aparecen en el orden de su primera compra.",
      ],
      expectedResult: "12 clientes distintos",
      finalFormula: "=UNICOS(VentasMD[Cliente])",
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Combinaciones ciudad–vendedor",
      description: `En <strong>${ANCHORS[1]}</strong>: muestra cada combinación <strong>Ciudad + Vendedor</strong> que existe en las ventas, sin repetir (2 columnas).`,
      solution: [
        `Selecciona ${F(ANCHORS[1])}.`,
        `Si le das varias columnas, UNICOS quita las filas repetidas completas: ${F("VentasMD[[Ciudad]:[Vendedor]]")}.`,
        `Escribe ${F("=UNICOS(VentasMD[[Ciudad]:[Vendedor]])")}.`,
      ],
      expectedResult: "Pares ciudad–vendedor sin repetir",
      finalFormula: "=UNICOS(VentasMD[[Ciudad]:[Vendedor]])",
    },
    {
      id: 3,
      level: "basico",
      title: "Tarea 3: Clientes de una sola compra",
      description: `En <strong>${ANCHORS[2]}</strong>: muestra solo los clientes que aparecen <strong>exactamente una vez</strong> en las ventas.`,
      solution: [
        `Selecciona ${F(ANCHORS[2])}.`,
        "UNICOS tiene un tercer argumento, exactamente_una_vez. Con VERDADERO devuelve solo los valores que no se repiten. El segundo argumento (por columna) se deja vacío.",
        `Escribe ${F("=UNICOS(VentasMD[Cliente],,VERDADERO)")}.`,
      ],
      expectedResult: "Clientes que solo compraron una vez",
      finalFormula: "=UNICOS(VentasMD[Cliente],,VERDADERO)",
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: Ciudades en orden alfabético",
      description: `En <strong>${ANCHORS[3]}</strong>: muestra las <strong>Ciudades</strong> sin repetir y <strong>ordenadas de la A a la Z</strong>.`,
      solution: [
        `Selecciona ${F(ANCHORS[3])}.`,
        "Primero UNICOS saca la lista y luego ORDENAR la acomoda.",
        `Escribe ${F("=ORDENAR(UNICOS(VentasMD[Ciudad]))")}.`,
      ],
      expectedResult: "Guadalajara, León, Mérida… en orden alfabético",
      finalFormula: "=ORDENAR(UNICOS(VentasMD[Ciudad]))",
    },
    {
      id: 5,
      level: "intermedio",
      title: "Tarea 5: ¿Cuántos clientes distintos?",
      description: `En <strong>${ANCHORS[4]}</strong>: devuelve el <strong>número</strong> de clientes distintos (un solo número, no la lista).`,
      solution: [
        `Selecciona ${F(ANCHORS[4])}.`,
        "CONTARA cuenta los elementos de la lista que devuelve UNICOS.",
        `Escribe ${F("=CONTARA(UNICOS(VentasMD[Cliente]))")}.`,
      ],
      expectedResult: "12",
      finalFormula: "=CONTARA(UNICOS(VentasMD[Cliente]))",
    },
    {
      id: 6,
      level: "intermedio",
      title: "Tarea 6: Clientes de una ciudad",
      description: `En <strong>${ANCHORS[5]}</strong>: muestra los clientes <strong>sin repetir</strong> de la ciudad escrita en <strong>J4</strong>.`,
      solution: [
        `Selecciona ${F(ANCHORS[5])}.`,
        "Primero FILTRAR deja solo los clientes de esa ciudad (con repetidos) y luego UNICOS los limpia.",
        `Escribe ${F("=UNICOS(FILTRAR(VentasMD[Cliente],VentasMD[Ciudad]=J4))")}.`,
      ],
      expectedResult: "Clientes de Monterrey",
      finalFormula: "=UNICOS(FILTRAR(VentasMD[Cliente],VentasMD[Ciudad]=J4))",
    },
    {
      id: 7,
      level: "reto",
      title: "Tarea 7: Total comprado por cliente",
      description: `En <strong>${ANCHORS[6]}</strong>, junto a la lista de la Tarea 1: devuelve el <strong>Importe total</strong> de cada cliente de esa lista. Usa el <strong>operador #</strong> para referirte a toda la lista derramada de ${ANCHORS[0]}, así el total crece solo si aparece un cliente nuevo.`,
      solution: [
        `Selecciona ${F(ANCHORS[6])}.`,
        `${F(`${ANCHORS[0]}#`)} significa \"todo lo que derrama ${ANCHORS[0]}\". Si se lo das como criterio a SUMAR.SI.CONJUNTO, calcula un total por cada cliente.`,
        `Escribe ${F(`=SUMAR.SI.CONJUNTO(VentasMD[Importe],VentasMD[Cliente],${ANCHORS[0]}#)`)}.`,
      ],
      expectedResult: "Un total por cada cliente de la lista",
      finalFormula: `=SUMAR.SI.CONJUNTO(VentasMD[Importe],VentasMD[Cliente],${ANCHORS[0]}#)`,
    },
  ],
};
